import {useMemo, useState} from "react";
import {Area, AreaChart, CartesianGrid, XAxis, YAxis} from "recharts";

import {Badge} from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {cn} from "@/lib/utils";

import {formatBucketLong, formatBucketTick, TIME_SERIES_COLORS} from "../lib";
import type {DashboardTimeSeries} from "../types";

type TimeSeriesChartProps = {
  data: DashboardTimeSeries;
  title?: string;
  description?: string;
};

/**
 * Multi-series area chart aligned to backend `buckets_local`.
 *
 * Important contract rule: the X-axis uses backend bucket strings as-is.
 * No date math runs on the client — DST and cross-day rounding stay
 * consistent with the company timezone.
 */
export function TimeSeriesChart({
  data,
  title = "Activity over time",
  description,
}: TimeSeriesChartProps) {
  const {chartConfig, chartData, seriesKeys} = useMemo(() => {
    const series = data.series.map((s, idx) => ({
      ...s,
      chartKey: `${s.key || "series"}-${idx}`,
      color: TIME_SERIES_COLORS[idx % TIME_SERIES_COLORS.length],
    }));

    const config: ChartConfig = Object.fromEntries(
      series.map((s) => [
        s.chartKey,
        {
          label: s.label || s.key,
          color: s.color,
        },
      ]),
    );

    const rows = data.buckets_local.map((bucket, i) => {
      const row: Record<string, string | number> = {bucket};
      series.forEach((s) => {
        row[s.chartKey] = s.data[i] ?? 0;
      });
      return row;
    });

    return {
      chartConfig: config,
      chartData: rows,
      seriesKeys: series.map((s) => s.chartKey),
    };
  }, [data]);

  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (chartData.length === 0 || seriesKeys.length === 0) {
    return (
      <Card className="py-4">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
        <div className="px-6 pb-6">
          <p className="text-muted-foreground text-sm">
            No activity recorded for the selected period.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden gap-0 py-0">
      <CardHeader className="border-b px-4 py-4 sm:flex sm:flex-row sm:items-start sm:justify-between md:px-5">
        <div className="flex flex-col gap-1">
          <CardTitle className="text-xl font-bold tracking-tight">{title}</CardTitle>
          {description ? <CardDescription className="text-sm">{description}</CardDescription> : null}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {seriesKeys.map((key) => {
            const isHidden = hidden.has(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggle(key)}
                className="cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring rounded"
                aria-pressed={!isHidden}
              >
                <Badge
                  variant={isHidden ? "outline" : "secondary"}
                  className={cn(
                    "h-7 gap-1.5 px-2.5 text-xs font-semibold transition-opacity",
                    isHidden && "opacity-60",
                  )}
                >
                  <span
                    className="inline-block size-2 rounded-full"
                    style={{
                      backgroundColor: chartConfig[key]?.color as string,
                    }}
                  />
                  {String(chartConfig[key]?.label ?? key)}
                </Badge>
              </button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="px-2 pb-4 pt-4 sm:px-4 md:px-5">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[320px] w-full"
        >
          <AreaChart accessibilityLayer data={chartData} margin={{top: 12, left: 0, right: 8, bottom: 4}}>
            <defs>
              {seriesKeys.map((key) => (
                <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={chartConfig[key]?.color as string}
                    stopOpacity={0.35}
                  />
                  <stop
                    offset="95%"
                    stopColor={chartConfig[key]?.color as string}
                    stopOpacity={0.02}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="bucket"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={formatBucketTick}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={32}
              tickFormatter={(value) => `${value}`}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => formatBucketLong(String(value))}
                  className="min-w-[180px]"
                />
              }
            />
            {seriesKeys
              .filter((k) => !hidden.has(k))
              .map((key) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={chartConfig[key]?.color as string}
                  fill={`url(#gradient-${key})`}
                  strokeWidth={2}
                  isAnimationActive={false}
                  activeDot={{r: 4}}
                />
              ))}
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
